import type { ContextSource, CopilotRunMeta } from '@fos/shared/contracts/copilot';
import { CONTEXT_SOURCE_TYPE_LABELS } from '@fos/shared/labels';
import { Sparkles } from 'lucide-react';
import { useId, useState } from 'react';
import { Link } from 'react-router';
import { cn } from '../../../lib/cn';
import { formatDateShort } from '../../../lib/format';
import { metaLine, sourceHref } from '../copilot-ui';

/**
 * Podpis wyniku AI (Figma 05 „W skrócie”: ikona sparkles + „Podsumowanie AI, dziś 07:30”), z oznaczeniem symulacji
 * i rozwijaną listą źródeł („Na podstawie: …” — typ, tytuł, data; linki do rekordów w zakresie osoby).
 */
export function CopilotFootnote({
  kind,
  meta,
  sources = [],
  className,
}: {
  /** „Podsumowanie AI”, „Szkic AI” */
  kind: string;
  meta: CopilotRunMeta;
  sources?: readonly ContextSource[];
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const shown = sources;
  return (
    <div className={cn('text-caption text-fg-muted', className)}>
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
        <Sparkles size={13} strokeWidth={1.75} aria-hidden className="shrink-0" />
        <span>{metaLine(kind, meta)}</span>
        {shown.length > 0 && (
          <>
            <span aria-hidden>·</span>
            <button
              type="button"
              aria-expanded={open}
              aria-controls={listId}
              onClick={() => setOpen((v) => !v)}
              className="rounded-xs text-fg-secondary hover:text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent"
            >
              {open ? 'Ukryj źródła' : `Źródła (${shown.length})`}
            </button>
          </>
        )}
      </p>
      {meta.fallbackReason && <p className="mt-1">{meta.fallbackReason}</p>}
      {open && shown.length > 0 && (
        <ul id={listId} aria-label="Źródła" className="mt-2 flex flex-col gap-1">
          {shown.map((s) => {
            const href = sourceHref(s);
            const label = `${CONTEXT_SOURCE_TYPE_LABELS[s.type]}: ${s.title.replace(/^[^:]+:\s*/, '')}`;
            return (
              <li key={`${s.type}:${s.id ?? s.title}`} className="flex min-w-0 items-baseline gap-2">
                {href ? (
                  <Link to={href} className="min-w-0 truncate text-fg-secondary hover:text-fg hover:underline">
                    {label}
                  </Link>
                ) : (
                  <span className="min-w-0 truncate text-fg-secondary">{label}</span>
                )}
                {s.date && <span className="shrink-0">{formatDateShort(s.date)}</span>}
                {s.untrusted && <span className="shrink-0">· dane klienta</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
